import { RefObject, useEffect } from "react";
import { useKeyboard } from "@/contexts/KeyboardContext";

export const IGNORE_KEYBOARD_AWARE_INPUT_CLASS = "ignoreKeyboardAwareInput";

interface KeyboardAwareInputProps {
  inputRef?: RefObject<HTMLIonInputElement>;
  containerRef?: RefObject<HTMLIonContentElement>;
}

/**
 * Custom hook for handling keyboard-aware input behavior in mobile applications.
 *
 * This hook automatically adjusts the viewport when the keyboard appears to ensure input fields
 * remain visible and accessible. It works on both native platforms (via Capacitor) and web platforms.
 *
 * The hook provides two main behaviors:
 * 1. For specific input/container pairs: Scrolls the container to bottom when the input is focused
 * 2. For general inputs: Scrolls the focused input into view with smooth animation
 *
 * @param inputRef - Reference to the input element
 * @param containerRef - Reference to the content container
 */
const useKeyboardAwareInput = (
  { inputRef, containerRef }: KeyboardAwareInputProps = {},
) => {
  const { isKeyboardOpen } = useKeyboard();

  useEffect(() => {
    const activeElement = document?.activeElement satisfies Element | null;
    const activeIonInput = inputRef?.current;
    const activeIonInputContainer = containerRef?.current;

    if (!isKeyboardOpen || !activeElement) {
      return;
    }

    /**
     * Handles keyboard-aware scrolling behavior for input fields within constrained spaces.
     * This implementation ensures the input field remains visible when the keyboard appears,
     * particularly useful in modal contexts where bottom space is limited.
     *
     * @note The default hook behavior may not work optimally in modal contexts due to space constraints.
     * This implementation provides a more reliable solution for such scenarios.
     */
    if (
      activeIonInput &&
      activeIonInputContainer &&
      activeIonInput.contains(activeElement)
    ) {
      const timer = setTimeout(() => {
        activeIonInputContainer.scrollToBottom(421);
      }, 421);
      return () => clearTimeout(timer);
    }

    if (
      activeElement.tagName === "INPUT" &&
      !activeElement.hasAttribute("readonly") &&
      !activeElement.classList.contains(
        IGNORE_KEYBOARD_AWARE_INPUT_CLASS,
      )
    ) {
      const timer = setTimeout(() => {
        activeElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 421);
      return () => clearTimeout(timer);
    }
  }, [isKeyboardOpen]);

  return null;
};

export default useKeyboardAwareInput;
